import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useToast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';

// 설정 관련 모달 컴포넌트들
import { PhoneChangeModal } from '@/components/settings/phone/PhoneChangeModal';
import { EmailChangeModal } from '@/components/settings/email/EmailChangeModal';
import { PasswordChangeModal } from '@/components/settings/password/PasswordChangeModal';
import { AccountDeletionModal } from '@/components/settings/account/AccountDeletionModal';

interface SellerData {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  business_name?: string;
  business_registration_number?: string;
}

export function SellerSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  const [sellerData, setSellerData] = useState<SellerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 모달 상태들
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAccountDeletionModal, setShowAccountDeletionModal] = useState(false);

  // 판매자 데이터 가져오기
  useEffect(() => {
    const fetchSellerData = async () => {
      if (!user?.id || user.role !== 'seller') return;

      try {
        // users와 sellers 테이블 조인하여 데이터 가져오기
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, email, phone, name, role')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Failed to fetch user data:', userError);
          showToast('사용자 정보를 불러오는데 실패했습니다');
          return;
        }

        // businesses 테이블에서 추가 정보 가져오기
        const { data: sellerInfo, error: sellerError } = await supabase
          .from('businesses')
          .select('business_name, business_number')
          .eq('owner_id', user.id)
          .single();

        if (sellerError) {
          console.error('Failed to fetch seller info:', sellerError);
          // 판매자 정보가 없어도 기본 사용자 정보는 표시
        }

        setSellerData({
          ...userData,
          business_name: sellerInfo?.business_name,
          business_registration_number: sellerInfo?.business_number
        });
      } catch (error) {
        console.error('Error fetching seller data:', error);
        showToast('사용자 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSellerData();
  }, [user, showToast]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefreshData = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, phone, name, role')
        .eq('id', user.id)
        .single();

      if (userError) {
        showToast('데이터 새로고침에 실패했습니다');
        return;
      }

      const { data: sellerInfo } = await supabase
        .from('sellers')
        .select('business_name, business_registration_number')
        .eq('user_id', user.id)
        .single();

      setSellerData({
        ...userData,
        business_name: sellerInfo?.business_name,
        business_registration_number: sellerInfo?.business_registration_number
      });
      showToast('정보가 업데이트되었습니다');
    } catch (error) {
      showToast('데이터 새로고침에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
            <p className="text-[#666666] text-base">설정 정보를 불러오는 중...</p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="seller" minimalLoading={true}>
      <SellerPageContainer activeNavId="mypage">
        <div className="flex flex-col h-full bg-white">
          {/* Header */}
          <header className="flex items-center justify-center py-4 relative mb-6">
            <button
              onClick={handleBack}
              className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img
                src="/icons/icon_back.svg"
                alt="뒤로가기"
                className="w-6 h-6"
              />
            </button>
            <h1 className="text-[24px] font-bold text-[#222222]">설정</h1>
          </header>

          {/* Content */}
          <main className="flex-1 pb-6">
            {sellerData && (
              <div className="space-y-6">
                {/* 이메일 설정 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        이메일
                      </label>
                      <p className="text-base text-gray-900 truncate">
                        {sellerData.email}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowEmailModal(true)}
                      className="ml-4 px-4 py-2 bg-[#4CD964] text-white text-sm font-medium rounded-lg hover:bg-[#45B85C] transition-colors"
                    >
                      변경
                    </button>
                  </div>
                </div>

                {/* 비밀번호 설정 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        비밀번호
                      </label>
                      <p className="text-base text-gray-600">
                        ••••••••
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      className="ml-4 px-4 py-2 bg-[#4CD964] text-white text-sm font-medium rounded-lg hover:bg-[#45B85C] transition-colors"
                    >
                      변경
                    </button>
                  </div>
                </div>

                {/* 전화번호 설정 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        전화번호
                      </label>
                      <p className="text-base text-gray-900">
                        {sellerData.phone || '등록되지 않음'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPhoneModal(true)}
                      className="ml-4 px-4 py-2 bg-[#4CD964] text-white text-sm font-medium rounded-lg hover:bg-[#45B85C] transition-colors"
                    >
                      변경
                    </button>
                  </div>
                </div>

                {/* 사업자 정보 (판매자 전용) */}
                {sellerData.business_name && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">사업자 정보</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          상호명
                        </label>
                        <p className="text-sm text-gray-900">
                          {sellerData.business_name}
                        </p>
                      </div>
                      {sellerData.business_registration_number && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            사업자등록번호
                          </label>
                          <p className="text-sm text-gray-900">
                            {sellerData.business_registration_number}
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      사업자 정보 변경은 고객센터로 문의해주세요
                    </p>
                  </div>
                )}

                {/* 탈퇴하기 버튼 */}
                <div className="pt-8">
                  <button
                    onClick={() => setShowAccountDeletionModal(true)}
                    className="text-sm text-gray-500 hover:text-red-600 transition-colors underline"
                  >
                    탈퇴하기
                  </button>
                </div>
              </div>
            )}
          </main>

          {/* 모달들 */}
          <PhoneChangeModal
            isOpen={showPhoneModal}
            onClose={() => setShowPhoneModal(false)}
            currentPhone={sellerData?.phone || ''}
            userId={sellerData?.id || ''}
            onSuccess={() => {
              handleRefreshData();
              showToast('전화번호가 변경되었습니다');
            }}
          />
          
          <EmailChangeModal
            isOpen={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            currentEmail={sellerData?.email || ''}
            userId={sellerData?.id || ''}
            userName={sellerData?.name || ''}
            onSuccess={() => {
              handleRefreshData();
              showToast('이메일이 변경되었습니다');
            }}
          />
          
          <PasswordChangeModal
            isOpen={showPasswordModal}
            onClose={() => setShowPasswordModal(false)}
            userEmail={sellerData?.email || ''}
            userName={sellerData?.name || ''}
            onSuccess={() => {
              setShowPasswordModal(false);
              showToast('비밀번호가 변경되었습니다');
            }}
          />
          
          <AccountDeletionModal
            isOpen={showAccountDeletionModal}
            onClose={() => setShowAccountDeletionModal(false)}
            userRole="seller"
            userData={sellerData}
          />

          <ToastContainer />
        </div>
      </SellerPageContainer>
    </ProtectedRoute>
  );
}